/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module SharedModule
 */

import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnInit,
    Output,
    QueryList,
    SimpleChanges,
    TemplateRef,
    ViewChild,
    ViewChildren,
} from '@angular/core';

import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import { L10nService, VIPModule } from '@vmw/ngx-vip';
import { FocusKeyManager } from '@angular/cdk/a11y';
import { CommonModule } from '@angular/common';
import { ClrSpinnerModule } from '@clr/angular';
import { Subject } from 'rxjs';
import {
    containsDropdownOptionByValue,
    getDisplayLabel,
    getOptionsHeight,
    getValue,
} from '../avi-dropdown.utils';
import { DropdownModelSingleValue, IAviDropdownOption } from '../avi-dropdown.types';
import {
    MAX_OPTIONS_LIST_HEIGHT,
    OPTION_HEIGHT,
} from '../avi-dropdown.constants';
import './avi-dropdown-options.component.less';
import * as l10n from './avi-dropdown-options.l10n';
import { AviDropdownOptionComponent } from './avi-dropdown-option/avi-dropdown-option.component';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * @description Component for the dropdown options.
 * @author alextsg, Alex Klyuev
 */
@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { role: 'list' },
    selector: 'avi-dropdown-options',
    templateUrl: './avi-dropdown-options.component.html',
    standalone: true,
    imports: [
        AviDropdownOptionComponent,
        ScrollingModule,
        CommonModule,
        ClrSpinnerModule,
        VIPModule,
    ],
})
export class AviDropdownOptionsComponent<
    TDropdownOptionValue = DropdownModelSingleValue
> implements OnInit, OnChanges {
    /**
     * List of dropdown options to show in the menu.
     */
    @Input() public options: Array<IAviDropdownOption<TDropdownOptionValue>> = [];

    /**
     * List of dropdown options that have been selected.
     */
    @Input() public selectedOptions: IAviDropdownOption[] = [];

    /**
     * Width used for the viewport dimensions.
     */
    @Input() public width: number;

    /**
     * If true, shows a spinner under the options.
     */
    @Input() public busy = false;

    /**
     * Hides dropdown when there are no options.
     */
    @Input()
    public hideIfNoOptions = false;

    /**
     * Template ref for custom option templates.
     */
    @Input()
    public optionTemplateRef?: TemplateRef<HTMLElement>;

    /**
     * Optional signpost templateRef.
     */
    @Input()
    public optionSignpostTemplateRef?: TemplateRef<HTMLElement>;

    /**
     * Message to display if there are no options.
     */
    @Input()
    public noEntriesMessage?: string;

    /**
     * Error message to display.
     */
    @Input()
    public errorMessage?: string;

    /**
     * Track number of options for sizing purposes.
     */
    @Input()
    public optionsLengthSubject: Subject<void> = new Subject<void>();

    /**
     * Event emitter for selecting an option.
     */
    @Output() public onOptionSelect = new EventEmitter<IAviDropdownOption<TDropdownOptionValue>>();

    /**
     * Event emitter for user submission without selecting an option.
     */
    @Output() public onSubmit = new EventEmitter<void>();

    /**
     * Event emitter for scrolling to the end of the list.
     */
    @Output() public onScrollEnd = new EventEmitter<number>();

    @Output()
    public onEscape = new EventEmitter<void>();

    @ViewChild(CdkVirtualScrollViewport) private viewport: CdkVirtualScrollViewport;

    @ViewChildren(AviDropdownOptionComponent)
    private items: QueryList<AviDropdownOptionComponent>;

    /**
     * Height of each dropdown option. Passed to the virtual scrolling CDK.
     */
    public optionHeight = OPTION_HEIGHT;

    /**
     * Index of the option at the top of the viewport.
     */
    public scrollIndex = 0;

    /**
     * Index of the currently highlighted option.
     */
    public highlightedIndex: number | null = null;

    /**
     * Don't highlight any fields when true.
     */
    public ignoreHighlight = true;

    /**
     * Ignore mouse enter events when true.
     */
    public ignoreMouseEnter = false;

    /**
     * When true, set highlighted index to null on mouse leave.
     */
    public resetHighlightIndexOnMouseLeave = false;

    /**
     * Initialize height as a single option since an empty list will still have an empty placeholder
     * with a height of 1 option.
     */
    public height = OPTION_HEIGHT;

    /**
     * Get keys from source bundles for template usage
     */
    public readonly l10nKeys = l10nKeys;

    private keyManager: FocusKeyManager<AviDropdownOptionComponent>;

    constructor(l10nService: L10nService) {
        l10nService.registerSourceBundles(dictionary);
    }

    /**
     * Remove highlights when mouse leaves dropdown.
     */
    @HostListener('mouseleave')
    private handleMouseLeave(): void {
        if (this.resetHighlightIndexOnMouseLeave) {
            this.highlightedIndex = null;
        }

        this.ignoreHighlight = true;
    }

    /**
     * Handle keyboard controls.
     */
    @HostListener('keydown', ['$event'])
    private onKeydown(event: KeyboardEvent): void {
        switch (event.key) {
            case 'Enter':
            case ' ':
                this.keyManager.activeItem.handleSelect();
                event.stopPropagation();
                event.preventDefault();
                break;
            case 'Escape':
                event.stopPropagation();
                event.preventDefault();
                this.onEscape.emit();
                break;
            default:
                this.keyManager.onKeydown(event);
        }
    }

    public ngAfterViewInit(): void {
        this.keyManager = new FocusKeyManager(this.items).withWrap();
    }

    /** @override */
    public ngOnInit(): void {
        // Avoid condition when top choice is highlighted then unhighlighted while
        // dropdown is being constructed.
        this.resetHighlightIndexOnMouseLeave = true;
        setTimeout(() => this.resetHighlightIndexOnMouseLeave = false, 200);
    }

    /** @override */
    public ngOnChanges(changes: SimpleChanges): void {
        const { options } = changes;

        if (options) {
            const { previousValue, currentValue } = options;
            const newOptionsLength = currentValue?.length || 0;
            const previousOptionsLength = previousValue?.length || 0;

            if (previousOptionsLength !== newOptionsLength) {
                this.optionsLengthSubject.next();

                this.height = getOptionsHeight(
                    newOptionsLength,
                    OPTION_HEIGHT,
                    MAX_OPTIONS_LIST_HEIGHT,
                );

                setTimeout(() => this.viewport.checkViewportSize());
            }

            // if no options, remove the highlight so that no option can be selected
            // by keyboard actions
            if (newOptionsLength === 0) {
                this.ignoreHighlight = true;
            }
        }
    }

    /**
     * Update the highlighted option index when the mouse hovers over it.
     */
    public handleMouseEnter(index: number): void {
        if (this.ignoreMouseEnter) {
            return;
        }

        this.ignoreHighlight = false;
        this.highlightedIndex = index;
    }

    /**
     * Update the highlighted index from mouse movements when mouseenter is ignored.
     */
    public handleMouseMove(index: number): void {
        if (this.ignoreMouseEnter) {
            this.highlightedIndex = index;
            this.ignoreMouseEnter = false;
        }
    }

    /**
     * Handler for selecting a dropdown option.
     */
    public handleSelect(dropdownOption: IAviDropdownOption<TDropdownOptionValue>): void {
        this.onOptionSelect.emit(dropdownOption);
    }

    /**
     * Returns the text to be shown based on the DropdownOption.
     */
    public getOptionLabel(dropdownOption: IAviDropdownOption): string {
        return getDisplayLabel(dropdownOption);
    }

    /**
     * Returns true if the dropdownOption is selected.
     */
    public isSelected(dropdownOption: IAviDropdownOption): boolean {
        return containsDropdownOptionByValue(dropdownOption, this.selectedOptions);
    }

    /**
     * Returns true if the dropdownOption is highlighted.
     */
    public isHighlighted(index: number): boolean {
        if (this.ignoreHighlight) {
            return false;
        }

        return index === this.highlightedIndex;
    }

    /**
     * Returns the dropdown value from the dropdownOption object. Used as the trackBy function.
     */
    // eslint-disable-next-line class-methods-use-this
    public trackByValue(
        index: number,
        dropdownOption: IAviDropdownOption<TDropdownOptionValue>,
    ): IAviDropdownOption<TDropdownOptionValue>['value'] {
        return getValue(dropdownOption);
    }

    /**
     * Called when the scrolling changes. The onScrollEnd event is emitted when the user scrolls to
     * the bottom of the options list.
     */
    public handleScrollIndexChange(index: number): void {
        this.scrollIndex = index;

        const { end } = this.viewport.getRenderedRange();
        const total = this.viewport.getDataLength();

        if (end >= total) {
            this.onScrollEnd.emit(total);
        }
    }

    /**
     * Use Angular CDK's keymanager to set the first dropdown action as active. Called externally
     * by parent component.
     */
    public setActive(): void {
        this.keyManager.setFirstItemActive();
    }
}
